/**
 * API Error Logger
 * Logs failed API requests to the error logging backend
 */

import axios from 'axios';
import { getStoredHouseholdId } from '../helpers/authStorage';
import { scrubSensitiveData } from '../utils/sentryScrubber';

const isErrorLoggingRequest = (context = {}) =>
    context.route === 'error-logging' && context.endpoint === 'log_error';

/**
 * Log an API error to the server
 * @param {Error} error - The error object
 * @param {Object} context - Additional context (route, endpoint, etc.)
 */
export const logApiError = async (error, context = {}) => {
    if (isErrorLoggingRequest(context)) {
        return;
    }

    // Skip logging for 401 errors — the global interceptor already cleared auth
    // tokens, so sending an authenticated log request would fail anyway.
    if (error.response?.status === 401) {
        return;
    }

    // Skip 409 on idempotent GETs. Our backend never returns 409 on GET (it's
    // reserved for write conflicts on POST/PATCH), so any 409 observed here
    // comes from an upstream proxy/CDN or transient network condition. The
    // caller's isError state already drives the UX, so logging these to
    // server_errors is non-actionable noise.
    if (
        error.response?.status === 409 &&
        (context.method || 'GET').toUpperCase() === 'GET'
    ) {
        return;
    }

    // The /log_error_auth_user endpoint is gated by authenticateToken; without
    // a token we'd POST `Bearer null`, get a 401, and then the global axios
    // 401 interceptor would force-redirect to /login. Anonymous pages (login,
    // onboarding, blog) and post-logout windows are the dominant source of
    // calls here, so bail early and fall back to console.
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
        console.error('[API-ERROR-LOGGER] No access token; skipping server log:', error);
        return;
    }

    try {
        const baseURL =
            process.env.NODE_ENV === "production" ? "/" : "http://localhost:5000/";
        const household_id = getStoredHouseholdId();

        // Determine error type
        let error_type = 'api_error';
        if (axios.isAxiosError(error)) {
            if (!error.response) {
                error_type = 'network_error';
            }
        }

        const safeContext = scrubSensitiveData(context) || {};
        const safeResponseData = scrubSensitiveData(error.response?.data);
        const safeRequestUrl = scrubSensitiveData(error.config?.url);

        // Extract error details
        const errorData = {
            household_id,
            error_type,
            error_message: scrubSensitiveData(error.message || 'Unknown API error'),
            error_stack: scrubSensitiveData(error.stack || null),
            url: scrubSensitiveData(window.location.href),
            user_action: `API Request: ${safeContext.method || 'GET'} ${safeContext.route || 'unknown'}/${safeContext.endpoint || 'unknown'}`,
            browser_info: {
                userAgent: navigator.userAgent,
                language: navigator.language,
                platform: navigator.platform,
                viewport: {
                    width: window.innerWidth,
                    height: window.innerHeight
                }
            },
            metadata: {
                ...safeContext,
                status: error.response?.status,
                statusText: error.response?.statusText,
                responseData: safeResponseData,
                requestUrl: safeRequestUrl,
                requestMethod: error.config?.method,
            }
        };

        // Send error log to server
        const logUrl = `${baseURL}api/v1/error-logging/log_error_auth_user`;
        
        await axios.post(logUrl, errorData, {
            headers: { Authorization: `Bearer ${accessToken}` }
        });
    } catch (loggingError) {
        // If logging fails, just console.error (don't create infinite loop)
        console.error('[API-ERROR-LOGGER] Failed to log API error:', loggingError);
    }
};

export default logApiError;


